/**
 * Plate allow-list sync for Hikvision DS-TCG406-E LPR cameras (ITCCAM, firmware V5.0.4).
 *
 * The camera holds its own allow-list ("Allowlist and Blocklist" in its web UI), so a plate
 * synced here opens the barrier even if Panther is unreachable at the moment of entry.
 *
 * Everything below was established against real hardware, because this firmware does not match
 * Hikvision's published ISAPI:
 *
 *   read   POST /ISAPI/Traffic/channels/{ch}/searchLPListAudit            (XML in, XML out)
 *   write  PUT  /ISAPI/Traffic/channels/{ch}/licensePlateAuditData/record?format=json
 *   caps   GET  /ISAPI/Traffic/channels/{ch}/searchLPListAudit/capabilities
 *          → licensePlate 1..16, cardNo 0..48, type ∈ {blackList, whitelist}
 *
 * Three behaviours worth knowing before changing anything here:
 *
 *   1. The write body must be a `LicensePlateInfoList` ARRAY carrying operationType/operation.
 *      A bare record is accepted and silently discarded.
 *   2. THE RESPONSE STATUS IS MEANINGLESS. The camera answers
 *      {"statusCode":1,"statusString":"OK"} to an empty body and to nonsense keys, and has been
 *      observed answering "Invalid Operation" to requests that did take effect. Success is only
 *      ever confirmed by re-reading the list.
 *   3. Read and write disagree: you write listType "allowList", the search returns "whiteList".
 */

/** licensePlate max from the camera's own searchLPListAudit/capabilities. */
export const LPR_PLATE_MAX_LENGTH = 16;

/** Constants the firmware requires on every record. Chinese-market defaults; accepted for
 *  Mercosul-format plates on the DS-TCG406-E we tested against. */
const PLATE_COLOR = 'blue';
const PLATE_TYPE = '92TypeCivil';
const CERTIFICATE_TYPE = 'ID';

export type LprListType = 'allowList' | 'blockList';

export interface LprCameraTarget {
  /** e.g. "http://192.168.1.50:1005" — no trailing slash. */
  baseUrl: string;
  username: string;
  password: string;
  /** ISAPI traffic channel; 1 on every DS-TCG406 seen so far. */
  channelId: number;
}

export interface LprPlateEntry {
  plate: string;
  name?: string;
  cardNo?: string;
  listType: LprListType;
}

export interface LprPlateInput {
  plate: string;
  /** Shown as "Belong to" in the camera UI — we put the vehicle owner's name here. */
  name?: string;
  cardNo?: string;
}

export interface LprHttpRequestConfig {
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  timeout: number;
  digestAuth: { username: string; password: string };
}

export interface LprHttpResult {
  success?: boolean;
  statusCode?: number;
  data?: string;
}

/** Injected transport. In the app this is Electron's deviceHttpRequest (digest auth over LAN);
 *  in tests it is a plain mock, which is why nothing here touches the network directly. */
export type LprHttpRequest = (config: LprHttpRequestConfig) => Promise<LprHttpResult>;

const DEFAULT_TIMEOUT_MS = 15000;

/**
 * Uppercases and strips separators so "abc-1234" and "ABC 1234" reconcile against the camera,
 * which stores plates bare. Returns null when the value cannot be a plate at all.
 */
export function normalizePlate(plate: string | null | undefined): string | null {
  if (typeof plate !== 'string') return null;
  const cleaned = plate.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!cleaned) return null;
  if (cleaned.length > LPR_PLATE_MAX_LENGTH) return null;
  return cleaned;
}

function channelPath(camera: LprCameraTarget, resource: string): string {
  return `${camera.baseUrl}/ISAPI/Traffic/channels/${camera.channelId}/${resource}`;
}

function digest(camera: LprCameraTarget) {
  return { username: camera.username, password: camera.password };
}

export function buildSearchRequest(
  camera: LprCameraTarget,
  opts: { maxResults: number; position: number },
): LprHttpRequestConfig {
  const body =
    '<?xml version="1.0" encoding="UTF-8"?>' +
    '<LPListAuditSearchDescription version="2.0" xmlns="http://www.isapi.org/ver20/XMLSchema">' +
    '<searchID>0</searchID>' +
    `<maxResults>${opts.maxResults}</maxResults>` +
    `<searchResultPosition>${opts.position}</searchResultPosition>` +
    '</LPListAuditSearchDescription>';

  return {
    url: channelPath(camera, 'searchLPListAudit'),
    method: 'POST',
    headers: { 'Content-Type': 'application/xml' },
    body,
    timeout: DEFAULT_TIMEOUT_MS,
    digestAuth: digest(camera),
  };
}

function tagValue(xml: string, tag: string): string {
  const m = new RegExp(`<${tag}>([^<]*)</${tag}>`).exec(xml);
  return m ? m[1].trim() : '';
}

/** "whiteList" (read) and "allowList" (write) are the same list. Normalize to the write word. */
function toListType(cameraType: string): LprListType {
  return /black/i.test(cameraType) ? 'blockList' : 'allowList';
}

/**
 * Parses a searchLPListAudit response. Never throws: an edge 502 returns an HTML page, and a
 * parse failure must surface as "no entries" to the caller's own error handling rather than as
 * an exception from a regex.
 */
export function parseSearchResponse(xml: string): { total: number; entries: LprPlateEntry[] } {
  if (typeof xml !== 'string' || !xml.includes('<LicensePlateInfoList')) {
    return { total: 0, entries: [] };
  }

  const total = parseInt(tagValue(xml, 'totalMatches') || '0', 10) || 0;
  const entries: LprPlateEntry[] = [];

  const blocks = xml.match(/<LicensePlateInfo>[\s\S]*?<\/LicensePlateInfo>/g) || [];
  for (const block of blocks) {
    const plate = tagValue(block, 'LicensePlate');
    if (!plate) continue;
    entries.push({
      plate,
      name: tagValue(block, 'name'),
      cardNo: tagValue(block, 'cardNo'),
      listType: toListType(tagValue(block, 'type')),
    });
  }

  return { total, entries };
}

/**
 * Builds the add request for a single plate.
 *
 * One plate per call: sync and revoke are always per-vehicle and the caller iterates its own
 * list. The `LicensePlateInfoList` envelope is still an array because the firmware requires it,
 * but it always carries exactly one record — the only shape verified against real hardware.
 */
export function buildAddRequest(
  camera: LprCameraTarget,
  entry: LprPlateInput,
): LprHttpRequestConfig {
  const plate = normalizePlate(entry.plate);
  if (!plate) {
    throw new Error(
      `Placa inválida para a câmera LPR: "${entry.plate}" (1 a ${LPR_PLATE_MAX_LENGTH} caracteres alfanuméricos)`,
    );
  }

  return {
    url: `${channelPath(camera, 'licensePlateAuditData/record')}?format=json`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      LicensePlateInfoList: [
        {
          id: '1',
          plateColor: PLATE_COLOR,
          plateType: PLATE_TYPE,
          listType: 'allowList',
          LicensePlate: plate,
          cardNo: entry.cardNo ?? '',
          cardID: '',
          name: entry.name ?? '',
          certificateType: CERTIFICATE_TYPE,
          certificateNumber: '',
          virtualParkingNum: '',
          operationType: 'add',
          operation: 'new',
        },
      ],
    }),
    timeout: DEFAULT_TIMEOUT_MS,
    digestAuth: digest(camera),
  };
}

/**
 * Builds the remove request.
 *
 * Captured from the camera's web UI. Deliberately different from the add call in both endpoint
 * and body shape — `DelLicensePlateAuditData` takes a `CompoundCond` matcher rather than the
 * record array `licensePlateAuditData/record` expects. Nothing in the add call hints at this,
 * so do not "simplify" the two into one shape.
 *
 * `deleteAllEnabled` is the camera's "Delete All" button. It is hardcoded false and no argument
 * can reach it: flipping it would wipe every plate on the device.
 */
export function buildRemoveRequest(camera: LprCameraTarget, plate: string): LprHttpRequestConfig {
  const normalized = normalizePlate(plate);
  if (!normalized) {
    // Never send a blank licensePlate — it is a wildcard the camera may apply to everything.
    throw new Error(`Placa inválida para remoção: "${plate}"`);
  }

  return {
    url: `${channelPath(camera, 'DelLicensePlateAuditData')}?format=json`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deleteAllEnabled: false,
      CompoundCond: { plateColor: '', licensePlate: normalized },
    }),
    timeout: DEFAULT_TIMEOUT_MS,
    digestAuth: digest(camera),
  };
}

/** Reads the camera's current allow-list. Throws when the camera cannot be read — an empty
 *  list and an unreachable camera must never look the same to a sync. */
export async function listPlates(
  camera: LprCameraTarget,
  request: LprHttpRequest,
  opts: { maxResults?: number; position?: number } = {},
): Promise<LprPlateEntry[]> {
  const result = await request(
    buildSearchRequest(camera, { maxResults: opts.maxResults ?? 500, position: opts.position ?? 0 }),
  );

  const body = result?.data ?? '';
  if (!body.includes('<LicensePlateInfoList')) {
    throw new Error('Não foi possível ler a lista de placas da câmera LPR');
  }

  return parseSearchResponse(body).entries;
}

/**
 * Adds a plate and CONFIRMS it by re-reading the list.
 *
 * The confirmation is the point of this function. The camera reports success for writes it
 * discards, so trusting the response is how a sync ends up reporting a vehicle as synced when
 * the camera stored nothing.
 */
export async function addPlate(
  camera: LprCameraTarget,
  request: LprHttpRequest,
  entry: LprPlateInput,
): Promise<void> {
  const plate = normalizePlate(entry.plate);
  if (!plate) {
    throw new Error(`Placa inválida para a câmera LPR: "${entry.plate}"`);
  }

  // Deliberately ignores the write's own status — see the note at the top of this file.
  await request(buildAddRequest(camera, entry));

  const onCamera = (await listPlates(camera, request)).some(e => e.plate === plate);
  if (!onCamera) {
    throw new Error(
      `A câmera não confirmou a gravação da placa ${plate}. ` +
        'A câmera pode reportar sucesso e descartar a gravação, por isso a lista é sempre relida.',
    );
  }
}

/**
 * Removes a plate and CONFIRMS it is gone by re-reading the list.
 *
 * Same reasoning as addPlate: the camera answers OK regardless, so a revoke that trusted the
 * response could leave a vehicle able to open the gate while the UI shows it as revoked.
 */
export async function removePlate(
  camera: LprCameraTarget,
  request: LprHttpRequest,
  plate: string,
): Promise<void> {
  const normalized = normalizePlate(plate);
  if (!normalized) {
    throw new Error(`Placa inválida para remoção: "${plate}"`);
  }

  await request(buildRemoveRequest(camera, normalized));

  const stillListed = (await listPlates(camera, request)).some(e => e.plate === normalized);
  if (stillListed) {
    throw new Error(
      `A câmera não confirmou a remoção da placa ${normalized}. ` +
        'A câmera pode reportar sucesso e manter a placa, por isso a lista é sempre relida.',
    );
  }
}
